"use client";
import { FiltersController } from "@components/commerce/product-listing/filters/context/FiltersContextProvider";
import { INITIAL_HITS_PER_PAGE } from "@components/commerce/product/list/product-list-footer/ProductListFooter";
import { EmptyResultSearchPage } from "@components/search/EmptyResultSearchPage";
import { SearchResultsPLP } from "@components/search/SearchResultsPLP";
import { getAdditionalSizesFilters } from "@lib/utils/plp/plpShoeSizes";
import { useStore } from "@store/index";
import { Box } from "@ecco/ui/box";
import { isEmpty } from "lodash-es";
import { useEffect } from "react";
import { Configure, useSearchBox } from "react-instantsearch";
import { DEFAULT_ATTRIBUTES_TO_RETRIEVE } from "@lib/algolia/constants";
import type { IPageProductListingPage } from "@contentful-api/types/contentful";
import type { RecommendationResult } from "@lib/algolia/types";

const queryHook = (query, search) => {
    if (query) {
        search(query);
    }
};

interface SearchPageTemplateProps {
    page: IPageProductListingPage;
    query: string;
    locale: string;
    trendingRecommendations: RecommendationResult[];
}

export const SearchPageTemplate = ({
    page,
    query,
    locale,
    trendingRecommendations,
}: SearchPageTemplateProps) => {
    const listOfFilters = [
        ...(page?.fields?.filtersList ?? []),
        ...getAdditionalSizesFilters(page?.fields?.filtersList),
    ];
    const customLinksCarousel = page?.fields?.customLinksCarousel;
    const sortByOptionsList = page?.fields?.sortByOptionsList || [];

    const { refine } = useSearchBox({
        queryHook,
    });

    useEffect(() => {
        refine(query as string);
    }, [query]);

    const lastLoadedPage = useStore((state) => state.data.products?.lastLoadedPage ?? 1);

    if (isEmpty(query)) {
        return (
            <Box
                px={["layoutMargin.mobile", "layoutMargin.tab", "layoutMargin.desktop"]}
                py={[10, 10, 16, 16]}
            >
                <EmptyResultSearchPage
                    showQueryFeedback={false}
                    trendingRecommendations={trendingRecommendations}
                    customLinksCarousel={customLinksCarousel}
                />
            </Box>
        );
    }

    return (
        <FiltersController
            listOfFilters={listOfFilters}
            sortByOptionsList={sortByOptionsList}
            page={lastLoadedPage}
        >
            <Configure
                query={query}
                facetFilters={"hasStock:true"}
                hitsPerPage={INITIAL_HITS_PER_PAGE * lastLoadedPage}
                attributesToRetrieve={DEFAULT_ATTRIBUTES_TO_RETRIEVE}
            />
            <SearchResultsPLP
                listOfFilters={listOfFilters}
                routerQuery={query}
                locale={locale}
                trendingRecommendations={trendingRecommendations}
                customLinksCarousel={customLinksCarousel}
            />
        </FiltersController>
    );
};
